import React, {useCallback, useEffect, useState} from 'react';
import AdminPage from '../../common/AdminPage/AdminPage';
import {
  modelMetadataOptionsLabels,
  useModelMetadataOptions,
  useModelMetadataOptionsUsage,
  modelMetadataLoadOptionsCount,
} from '../../../../../store/modelMetadata';
import {messageAdd, MessageTypes} from '../../../../../store/message';
import {Button, Tabs, Drawer} from 'antd';
import {PlusOutlined} from '@ant-design/icons';
import MetadataGrid from './Grid';
import {ModelMetadataOptionsDTOOptions, MetadataCountDTO} from 'hemwb-api';
import {useNavigate} from 'react-router-dom';
import {getUrlRoute, MRAppRoutes, useRoute} from '../../../../router/routes';
import Header from '../common/Header';
import {Search} from '../../../../core/Search/Search';
import {tid} from '../../../../../testUtils';
import SuspenseNull from '../../../../core/Suspense/SuspenseNull';
import {useLastValidValue} from '../../../../../hooks/useLastValidValue';
import {ManageMetadataActionProps} from '../common/ActionMenuTypes';
import {useDispatch} from 'react-redux';
import AddOption from '../common/addOption/AddOption';
import styles from './List.module.scss';
// ManageMetadataActionProps kept for selectedValues typing

// Properties to hide from the UI tabs
const HIDDEN_METADATA_PROPERTIES: Set<keyof ModelMetadataOptionsDTOOptions> = new Set([
  'mathematicaEnvironment',
  'pythonEnvironment',
]);

const getProperties = () =>
  (Object.keys(modelMetadataOptionsLabels) as (keyof ModelMetadataOptionsDTOOptions)[]).filter(
    (p) => !HIDDEN_METADATA_PROPERTIES.has(p),
  );

const List: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const {property} = useRoute<MRAppRoutes.MANAGE_METADATA_PROPERTY>();
  const fetchedOptions = useModelMetadataOptions();
  const options = useLastValidValue(fetchedOptions);
  const temporaryUsages = useModelMetadataOptionsUsage(property);
  const usages = useLastValidValue(temporaryUsages);

  const [tab, setTab] = useState<keyof ModelMetadataOptionsDTOOptions>();
  const [searchValue, setSearchValue] = useState('');
  const [selectedValues, setSelectedValues] = useState<Partial<ManageMetadataActionProps>>();
  const [optionsCount, setOptionsCount] = useState<MetadataCountDTO[]>();
  const [refreshOptionsCount, setRefreshOptionsCount] = useState<boolean>(false);
  const [showAddOptionDrawer, setShowAddOptionDrawer] = useState(false);

  useEffect(() => {
    setSelectedValues({value: undefined, retired: false});
    if (!optionsCount || refreshOptionsCount) {
      modelMetadataLoadOptionsCount(dispatch, {metadataType: 'file_tag'})
        .then((response) => {
          setOptionsCount(response);
          setRefreshOptionsCount(false);
        })
        .catch(() => {
          dispatch(messageAdd('Failed to report the issue', MessageTypes.ERROR));
        });
    }
  }, [property, options, dispatch, optionsCount, refreshOptionsCount]);
  const handleChangeTab = useCallback(
    (p: string) => {
      const nextTab = p as keyof ModelMetadataOptionsDTOOptions;
      // Prevent navigation to hidden properties
      if (!HIDDEN_METADATA_PROPERTIES.has(nextTab)) {
        setTab(nextTab);
        setSearchValue('');
        navigate(getUrlRoute(MRAppRoutes.MANAGE_METADATA_PROPERTY, {property: nextTab}), {replace: true});
      }
    },
    [navigate],
  );

  const handleSearchChange = useCallback((search: string) => {
    setSearchValue(search);
  }, []);

  const handleCloseAddOptionDrawer = useCallback(() => {
    setShowAddOptionDrawer(false);
  }, []);

  const handleAddOptionSuccess = useCallback(() => {
    handleCloseAddOptionDrawer();
    setRefreshOptionsCount(true);
  }, []);

  useEffect(() => {
    if (modelMetadataOptionsLabels && !tab) {
      const properties = getProperties();

      if (property in modelMetadataOptionsLabels) {
        setTab(property as keyof ModelMetadataOptionsDTOOptions);
      } else if (properties.length > 0) {
        setTab(properties[0]);
        navigate(getUrlRoute(MRAppRoutes.MANAGE_METADATA_PROPERTY, {property: properties[0]}), {replace: true});
      }
    }
  }, [navigate, tab, property]);

  if (!options || !tab) {
    return (
      <AdminPage style={{paddingLeft: '2rem'}} header={<Header />}>
        <SuspenseNull />
      </AdminPage>
    );
  }

  return (
    <>
      <AdminPage style={{paddingLeft: '2rem'}} header={<Header />} disableScroll={true}>
        <div style={{display: 'flex', flexDirection: 'column', height: '100%'}}>
          <div className={styles.metadataToolbar} style={{flexShrink: 0, borderBottom: '1px solid #dfe5ea'}}>
            <Tabs
              activeKey={tab}
              onChange={handleChangeTab}
              type="card"
              className={styles.metadataTabs}
              items={getProperties().map((p) => ({
                key: p,
                label: <span {...tid('tab', p)}>{modelMetadataOptionsLabels[p] || ''}</span>,
              }))}
            />
            <div className={styles.listPanel}>
              <Search value={searchValue} onValueChange={handleSearchChange} />
              <Button
                type="primary"
                style={{marginRight: '18px'}}
                size="large"
                icon={<PlusOutlined />}
                onClick={() => setShowAddOptionDrawer(true)}
                {...tid('btn', 'add-option')}
              >
                Add Option
              </Button>
            </div>
          </div>

          <div style={{flex: 1, overflowY: 'auto'}}>
            <MetadataGrid
              key={tab}
              options={options}
              selectedValues={selectedValues}
              onSelectChange={setSelectedValues}
              property={tab}
              usages={usages}
              fileTagCount={optionsCount}
              searchValue={searchValue}
            />
          </div>
        </div>

        <Drawer
          open={showAddOptionDrawer}
          onClose={handleCloseAddOptionDrawer}
          placement="right"
          width={420}
          className="whiteTheme"
          title={null}
          closable={false}
          styles={{body: {padding: '2.4rem', paddingTop: '1.6rem'}}}
        >
          {tab && (
            <AddOption
              property={tab}
              onSuccess={handleAddOptionSuccess}
              onCancel={handleCloseAddOptionDrawer}
              refreshOptionsCount={() => setRefreshOptionsCount(true)}
            />
          )}
        </Drawer>
      </AdminPage>
    </>
  );
};

export default List;
